import type { ComponentType, SVGProps } from 'react';
import { cn } from '../../lib/utils';
import {
  DocumentDuplicateIcon,
  EyeIcon,
  HeartIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';

type IconComponent = ComponentType<SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;

export interface PlanverseCardProps {
  title: string;
  imageUrl?: string;
  timestamp?: string;
  authorInitial?: string;
  likes?: number;
  duplicates?: number;
  views?: number;
  onClick?: () => void;
  className?: string;
}

function Stat({ icon: StatIcon, value }: { icon: IconComponent; value?: number }) {
  return (
    <span className="flex items-center gap-0.5">
      <StatIcon className="h-3.5 w-3.5" />
      <span className="text-[10px]">{value ?? 0}</span>
    </span>
  );
}

export default function PlanverseCard({
  title,
  imageUrl,
  timestamp,
  authorInitial = 'P',
  likes,
  duplicates,
  views,
  onClick,
  className,
}: PlanverseCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group flex flex-col gap-2 rounded-lg text-left font-display transition-opacity hover:opacity-95',
        className,
      )}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg bg-muted">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <PhotoIcon className="h-8 w-8" />
          </div>
        )}
      </div>

      <div className="flex w-full items-start gap-2">
        <span
          className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border text-[7.5px] text-white"
          style={{ backgroundColor: '#f79878', borderColor: '#f47950' }}
          aria-hidden
        >
          {authorInitial}
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p className="truncate text-sm text-foreground">{title}</p>
          {timestamp && (
            <p className="truncate text-xs text-muted-foreground">{timestamp}</p>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-1 text-muted-foreground">
          <Stat icon={HeartIcon} value={likes} />
          <Stat icon={DocumentDuplicateIcon} value={duplicates} />
          <Stat icon={EyeIcon} value={views} />
        </div>
      </div>
    </button>
  );
}
